import { Text } from "@smartsuite/react-ui/lib/components/typography/Text/Text";
import type { AppField, ID } from "@smartsuite/types";
import type { SimpleRecord } from "@smartsuite/types/lib/models/record";
import cx from "classnames";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { noop } from "rxjs";
import type { BaseRecord } from "../../../../../../../../types/form";
import {
  SimpleListVirtualScroll,
  type SimpleVirtualScrollRef,
} from "../SimpleVirtualScroll/SimpleVirtualScroll";
import { RecordItem } from "./RecordItem/RecordItem";

import "./RecordList.sass";

const MAX_VISIBLE_FIELDS = 4;
const COARSE_POINTER_QUERY = "(pointer: coarse)";

interface RecordListProps {
  coverImageField?: AppField;
  records: SimpleRecord[];
  fieldsToShow?: { [key: string]: AppField };
  linkedAppFields?: { [key: string]: AppField };
  selectedRecords: ID[];
  onRecordClick: (recordId: ID) => void;
  relatedRecords?: BaseRecord[];
  focusedItem: SimpleRecord;
  cursor: number;
}

const headerHeight = 44;
const itemHeight = 80;

export const RecordList: FC<RecordListProps> = ({
  coverImageField,
  records,
  fieldsToShow,
  linkedAppFields,
  selectedRecords,
  onRecordClick,
  relatedRecords,
  cursor,
  focusedItem,
}) => {
  const [height, setHeight] = useState(0);
  const virtualScrollRef = useRef<SimpleVirtualScrollRef>({
    scrollToIndex: noop,
  });
  const recordItemRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const offsetIndex = useMemo(() => {
    if (focusedItem) {
      const scrollBarHeight = wrapperRef.current?.offsetHeight;
      const itemsInView = Math.floor((scrollBarHeight ?? 0) / itemHeight);
      if (cursor < itemsInView) {
        return 0;
      }
      return cursor;
    }
  }, [cursor, focusedItem]);

  useEffect(() => {
    virtualScrollRef.current.scrollToIndex(offsetIndex ?? 0);
  }, [offsetIndex]);

  if (!fieldsToShow) {
    return null;
  }

  const listWidth = recordItemRef.current?.getBoundingClientRect().width;

  const visibleFieldSlugs = Object.keys(fieldsToShow).slice(0, MAX_VISIBLE_FIELDS);

  return (
    <div
      className="record-list"
      aria-label="Linked Records List"
      style={{ height: height + headerHeight }}
    >
      <div className="record-list__wrapper" ref={wrapperRef}>
        <SimpleListVirtualScroll
          className="record-list__scrollbar"
          containerClassName="record-list__scrollbar-body"
          ref={virtualScrollRef}
          items={records}
          additionalContentRender={
            <div
              className={cx("record-list__header", {
                "record-list__header_cover-image": !!coverImageField,
              })}
              style={
                {
                  "--list-width": `${listWidth}px`,
                  "--visible-fields-count": `${visibleFieldSlugs.length}`,
                } as React.CSSProperties
              }
            >
              {visibleFieldSlugs.map((fieldSlug) => {
                const field = fieldsToShow[fieldSlug];
                let title = field?.label;

                if (fieldSlug.includes(".")) {
                  const nestedField = linkedAppFields?.[fieldSlug.split(".")[0]]?.nested?.find(
                    (nested) => nested.slug === fieldSlug.split(".")[1],
                  );

                  if (nestedField) {
                    title = `${field?.label}: ${nestedField.label}`;
                  }
                }

                return (
                  <Text
                    key={fieldSlug}
                    className="record-list__title"
                    size="xs"
                    weight="semibold"
                    color="tertiary"
                    wrapMode="ellipsis"
                  >
                    {title}
                  </Text>
                );
              })}
            </div>
          }
          itemHeight={80}
          gutterRight={3}
          noScrollX={!window.matchMedia(COARSE_POINTER_QUERY).matches}
          noScrollY={false}
          onChangeHeight={setHeight}
        >
          {({ item: record }) => {
            return (
              <RecordItem
                recordItemRef={recordItemRef}
                coverImageField={coverImageField}
                key={record.id}
                fieldsToShow={fieldsToShow}
                record={record}
                isFocused={focusedItem?.id === record?.id}
                isSelected={selectedRecords.includes(record.id)}
                maxVisibleFields={MAX_VISIBLE_FIELDS}
                onClick={onRecordClick}
                relatedRecords={relatedRecords}
              />
            );
          }}
        </SimpleListVirtualScroll>
      </div>
    </div>
  );
};
